import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import './Marketplace.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Marketplace = () => {
  const navigate = useNavigate();
  const { sessionToken, user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    country: '',
    city: '',
    search: ''
  });

  useEffect(() => {
    loadMarketplace();
    if (sessionToken) {
      loadFavorites();
    }
  }, []);

  const loadMarketplace = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.country) params.append('country', filters.country);
      if (filters.city) params.append('city', filters.city);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API}/marketplace/brands?${params.toString()}`);
      
      if (response.data.success) {
        setBrands(response.data.brands);
      }
    } catch (error) {
      console.error('Error loading marketplace:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/favorites`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data.success) {
        const favIds = new Set(response.data.brands.map(b => b.id));
        setFavorites(favIds);
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const toggleFavorite = async (brandId) => {
    if (!sessionToken) {
      alert('Please login to add favorites');
      return;
    }

    try {
      if (favorites.has(brandId)) {
        await axios.delete(`${API}/marketplace/brands/${brandId}/favorite`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(brandId);
          return newSet;
        });
      } else {
        await axios.post(
          `${API}/marketplace/brands/${brandId}/favorite`,
          {},
          { headers: { Authorization: `Bearer ${sessionToken}` } }
        );
        setFavorites(prev => new Set(prev).add(brandId));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleSearch = () => {
    loadMarketplace();
  };

  return (
    <Layout>
      <div className="marketplace">
        <div className="marketplace-header">
          <h1>🛒 Brand Marketplace</h1>
          <p>Discover and shop from amazing fashion brands</p>
        </div>

        <div className="filters-section">
          <input
            type="text"
            placeholder="Search brands..."
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <input
            type="text"
            placeholder="Country"
            value={filters.country}
            onChange={(e) => setFilters({...filters, country: e.target.value})}
          />
          <input
            type="text"
            placeholder="City"
            value={filters.city}
            onChange={(e) => setFilters({...filters, city: e.target.value})}
          />
          <button className="btn-primary" onClick={handleSearch}>
            Search
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading brands...</div>
        ) : brands.length === 0 ? (
          <div className="empty-state">
            <h3>No brands found</h3>
            <p>Try adjusting your search filters</p>
          </div>
        ) : (
          <div className="brands-marketplace-grid">
            {brands.map(brand => (
              <div key={brand.id} className="marketplace-brand-card">
                <button 
                  className="favorite-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(brand.id);
                  }}
                >
                  {favorites.has(brand.id) ? '❤️' : '🤍'}
                </button>
                
                {brand.logo && (
                  <img src={brand.logo} alt={brand.name} className="brand-logo" />
                )}
                
                <h3>{brand.name}</h3>
                <p className="brand-description">{brand.description || 'Fashion brand'}</p>
                
                <div className="brand-location">
                  📍 {brand.city}, {brand.country}
                </div>
                
                <div className="brand-stats">
                  <span>📦 {brand.product_count} products</span>
                  <span>🏢 {brand.branch_count} locations</span>
                </div>
                
                <button 
                  className="btn-primary"
                  onClick={() => navigate(`/marketplace/brands/${brand.id}`)}
                >
                  View Store
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Marketplace;